import React from "react";
import { Link } from "react-router-dom";
import {
  TUTORIAL_SECTIONS,
  TUTORIAL_INDEX,
  getTutorialSection,
} from "../../data/tutorialContent";

/**
 * TutorialLayout — shared shell for /guide/:slug detail pages. JSX port of the
 * platform V2 TutorialLayout: a hero (eyebrow / title / description), a
 * breadcrumb back to the guide index, the page body, and a prev/next pager that
 * walks the TUTORIAL_SECTIONS order.
 *
 * Props: { lang, slug, eyebrow, title, description, children }
 */
export default function TutorialLayout({
  lang = "EN",
  slug,
  eyebrow,
  title,
  description,
  children,
}) {
  const isArabic = lang === "AR";
  const langKey = isArabic ? "ar" : "en";
  const idx = TUTORIAL_INDEX[langKey];

  const currentIndex = TUTORIAL_SECTIONS.indexOf(slug);
  const prevSlug = currentIndex > 0 ? TUTORIAL_SECTIONS[currentIndex - 1] : null;
  const nextSlug =
    currentIndex >= 0 && currentIndex < TUTORIAL_SECTIONS.length - 1
      ? TUTORIAL_SECTIONS[currentIndex + 1]
      : null;

  const sectionTitle = (s) => {
    const section = getTutorialSection(s);
    return section ? section[langKey].title : s;
  };

  const ChevronLeft = (
    <svg
      className={`h-4 w-4 text-gray-400 group-hover:text-emerald-700 ${isArabic ? "rotate-180" : ""}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
  const ChevronRight = (
    <svg
      className={`h-4 w-4 text-gray-400 group-hover:text-emerald-700 ${isArabic ? "rotate-180" : ""}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  );

  return (
    <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="mx-auto w-full max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav
          aria-label={isArabic ? "مسار التنقل" : "Breadcrumb"}
          className={`mb-6 flex items-center gap-2 text-sm text-gray-500 ${isArabic ? "flex-row-reverse" : ""}`}
        >
          <Link
            to="/guide"
            className="inline-flex items-center gap-1 hover:text-emerald-700"
          >
            {idx.breadcrumbHome}
          </Link>
          <span aria-hidden>/</span>
          <span className="text-gray-700">{title}</span>
        </nav>

        {/* Hero */}
        <header className={`mb-10 ${isArabic ? "text-right" : "text-left"}`}>
          {eyebrow ? (
            <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-emerald-700">
              {eyebrow}
            </p>
          ) : null}
          <h1 className="text-4xl font-bold leading-tight text-gray-900 sm:text-5xl">
            {title}
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-gray-600">{description}</p>
        </header>

        {/* Page body */}
        <article
          className={`max-w-none text-gray-700 ${isArabic ? "text-right" : "text-left"}`}
        >
          {children}
        </article>

        {/* Prev / Next pager */}
        <nav
          aria-label={isArabic ? "تنقل بين خطوات الشرح" : "Walkthrough navigation"}
          className="mt-16 flex flex-col gap-3 border-t border-gray-200 pt-8 sm:flex-row sm:items-center sm:justify-between"
        >
          {prevSlug ? (
            <Link
              to={`/guide/${prevSlug}`}
              className={`group inline-flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 transition hover:border-emerald-700 hover:shadow-sm ${isArabic ? "flex-row-reverse text-right" : ""}`}
            >
              {ChevronLeft}
              <div className={isArabic ? "text-right" : "text-start"}>
                <p className="text-xs text-gray-500">{idx.pagerPrev}</p>
                <p className="text-sm font-semibold text-gray-900">
                  {sectionTitle(prevSlug)}
                </p>
              </div>
            </Link>
          ) : (
            <span />
          )}
          {nextSlug ? (
            <Link
              to={`/guide/${nextSlug}`}
              className={`group inline-flex items-center justify-end gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 transition hover:border-emerald-700 hover:shadow-sm ${isArabic ? "flex-row-reverse text-right" : "text-end"}`}
            >
              <div>
                <p className="text-xs text-gray-500">{idx.pagerNext}</p>
                <p className="text-sm font-semibold text-gray-900">
                  {sectionTitle(nextSlug)}
                </p>
              </div>
              {ChevronRight}
            </Link>
          ) : (
            <span />
          )}
        </nav>
      </div>
    </main>
  );
}
